/**
 * Command action types
 * Defines what kind of action a command performs
 */
export const COMMAND_ACTIONS = {
  NAVIGATE: "navigate",
  ANALYZE: "analyze",
  CREATE: "create",
  VIEW: "view",
  SEARCH: "search",
  GENERATE: "generate",
  SUMMARIZE: "summarize",
};

/**
 * Context types for context-aware commands
 * Used to determine which Vuex state to read for context
 */
export const CONTEXT_TYPES = {
  PATIENT: "patient",
  PROVIDER: "provider",
  APPOINTMENT: "appointment",
  TASK: "task",
  // Future expansion: INVOICE, MEDICATION, etc.
};

/**
 * User roles for role-based command filtering
 */
export const USER_ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  PROVIDER: "provider",
};

/**
 * Category IDs
 */
export const CATEGORY_IDS = {
  NAVIGATION: "quick-navigation",
  PATIENT_INSIGHTS: "patient-insights",
  CLINICAL_DECISIONS: "clinical-decisions",
  INTERVENTIONS_OUTCOMES: "interventions-outcomes",
  CASELOAD_WORKFLOW: "caseload-workflow",
  DATA_ANALYTICS: "data-analytics",
  COMMUNICATION_COLLABORATION: "communication-collaboration",
  COMPLIANCE_BILLING: "compliance-billing",
  KNOWLEDGE_GUIDANCE: "knowledge-guidance",
  QUICK_ACTIONS: "quick-actions",
};

/**
 * Command IDs - organized by category
 */
export const COMMAND_IDS = {
  // Navigation & Actions commands - Global
  NAV_CLIENTS: "nav-clients",
  NAV_BOOKINGS: "nav-bookings",
  NAV_REPORTS_BILLING: "nav-reports-billing",
  NAV_REPORTS_PAYROLL: "nav-reports-payroll",
  NAV_PROVIDERS: "nav-providers",
  NAV_SETTINGS: "nav-settings",
  QA_NEW_TASK: "qa-new-task",
  QA_CREATE_ROOM: "qa-create-room",
  QA_NEW_CLIENT: "qa-new-client",
  QA_NEW_PROVIDER: "qa-new-provider",

  // Navigation & Actions commands - Client Context
  CLIENT_NAV_PROVIDERS: "client-nav-providers",
  CLIENT_NAV_CONTACTS: "client-nav-contacts",
  CLIENT_NAV_DOCUMENTS: "client-nav-documents",
  CLIENT_NAV_NOTES: "client-nav-notes",
  CLIENT_NAV_BUDGET: "client-nav-budget",
  CLIENT_NAV_ENGAGEMENT_NOTES: "client-nav-engagement-notes",
  CLIENT_NAV_SERVICES: "client-nav-services",
  CLIENT_NAV_PROFILE: "client-nav-profile",

  // Client Intelligence commands - Generic
  CI_NEED_FOLLOWUP: "ci-need-followup",
  CI_HIGH_RISK: "ci-high-risk",
  CI_RECENT_NOTES: "ci-recent-notes",

  // Client Intelligence commands - Context-aware (patient page)
  CI_OVERVIEW: "ci-overview",
  CI_LATEST_UPDATE: "ci-latest-update",
  CI_CLINICAL_SNAPSHOT: "ci-clinical-snapshot",
  CI_TREATMENT_SUMMARY: "ci-treatment-summary",
};

/**
 * Quick Action IDs
 */
export const QUICK_ACTION_IDS = {
  TODAYS_CALENDAR: "todays-calendar",
  NAV_CLIENTS: "nav-clients",
  ALERTS: "alerts",
  DASHBOARD: "dashboard",
  QA_NEW_CLIENT: "qa-new-client",
  BOOKINGS: "bookings",
};

/**
 * Context path mappings for different context types
 * Maps context type to Vuex state path
 */
export const CONTEXT_PATHS = {
  [CONTEXT_TYPES.PATIENT]: "patients.patient",
  [CONTEXT_TYPES.PROVIDER]: "providers.currentProvider",
  [CONTEXT_TYPES.APPOINTMENT]: "calendar.selectedAppointment",
  [CONTEXT_TYPES.TASK]: "tasks.selectedTask",
  // Future expansion ready
};

/**
 * Template placeholder patterns
 * Used for string replacement in context-aware commands
 */
export const TEMPLATE_PLACEHOLDERS = {
  PATIENT_NAME: "{patientName}",
  PATIENT_ID: "{patientId}",
  PROVIDER_NAME: "{providerName}",
  APPOINTMENT_DATE: "{appointmentDate}",
  // Future placeholders
};

/**
 * Oliver Bot Theme Colors
 * Extracted from existing bot components for consistent styling
 */
export const BOT_THEME = {
  // Primary gradient (from popover background)
  gradientStart: "#6ee7f9", // Cyan
  gradientMid: "#8f8ae6", // Purple
  gradientEnd: "#e5a3ff", // Light purple

  // Neutral colors
  white: "#ffffff",
  backgroundLight: "rgba(255, 255, 255, 0.9)",
  backgroundHover: "rgba(255, 255, 255, 1)",

  // Text colors
  textPrimary: "#1a1a1a",
  textSecondary: "#666",
  textLight: "#999",

  // Accent colors (for category icons)
  accentBlue: "#6ee7f9", // For navigation
  accentPurple: "#8f8ae6", // For intelligence/analysis
  accentPink: "#e5a3ff", // For actions

  // Shadows
  shadowLight: "rgba(143, 138, 230, 0.15)",
  shadowMedium: "rgba(143, 138, 230, 0.25)",
  shadowHeavy: "rgba(143, 138, 230, 0.3)",
};
